import {
  IFsLprProviderInterface,
  LPRResultsCameraModel,
  LPRResultsCameraVendor,
} from '../../../../../api';
import { ViLPRProvider } from '../../../../../shared/models';
import { extractCameraModelName } from '../../../../../utils';
import { LPRCameraModelsSelection } from '../../models';

export function calculateWorksWithAxonCount(
  providedCount: number,
  cameraModels: LPRResultsCameraModel[],
): number {
  if (providedCount !== null && providedCount !== undefined) {
    return providedCount;
  }

  if (cameraModels && cameraModels.length > 0) {
    return sumCameraModelCounts(cameraModels);
  }

  return 0;
}

/*
 * Count calculation priority (first available wins):
 * 1. Provider total (if no camera vendor filter)
 * 2. Component-provided count
 * 3. Camera vendor aggregate count
 * 4. Sum of individual camera model counts
 * 5. Provider total (fallback)
 */
export function calculateProviderCount(
  provider: IFsLprProviderInterface,
  cameraVendor: LPRResultsCameraVendor | undefined,
  providedCount: number,
  cameraModels: LPRResultsCameraModel[],
): number {
  if (!cameraVendor) {
    return provider?.total ?? 0;
  }

  if (providedCount !== null && providedCount !== undefined && providedCount > 0) {
    return providedCount;
  }

  if (cameraVendor.count !== null && cameraVendor.count !== undefined) {
    return cameraVendor.count;
  }

  if (cameraModels && cameraModels.length > 0) {
    const totalCameraModelCount = sumCameraModelCounts(cameraModels);
    if (totalCameraModelCount > 0) {
      return totalCameraModelCount;
    }
  }

  return provider?.total ?? 0;
}

export function sumCameraModelCounts(cameraModels: LPRResultsCameraModel[]): number {
  return cameraModels.reduce((total, model) => total + (model.count || 0), 0);
}

/*
 * Calculates how many results to exclude based on unselected camera models
 * Only applies to Axon VI and Works with Axon providers
 */
export function calculateCameraModelExclusions(
  provider: IFsLprProviderInterface,
  cameraModels: LPRResultsCameraModel[],
  cameraModelSelection: LPRCameraModelsSelection | undefined,
  isWorksWithAxon: boolean,
): number {
  if (!supportsCameraModelFiltering(provider, isWorksWithAxon)) {
    return 0;
  }

  if (!cameraModels?.length || !cameraModelSelection?.cameraModels?.size) {
    return 0;
  }

  // Guard against applying selection from different provider
  const selectionProviderName = cameraModelSelection.providerName;
  const isSelectionForThisProvider =
    (isWorksWithAxon && selectionProviderName === ViLPRProvider.WORKS_WITH_AXON) ||
    (!isWorksWithAxon && selectionProviderName === provider.nameEnum);

  if (!isSelectionForThisProvider) {
    return 0;
  }

  // Empty selection means nothing is filtered out
  const hasAnySelectedCameraModel = Array.from(cameraModelSelection.cameraModels.values()).some(
    (isSelected) => isSelected,
  );

  if (!hasAnySelectedCameraModel) {
    return 0;
  }

  let exclusionCount = 0;

  cameraModelSelection.cameraModels.forEach((isSelected, cameraModelInSelection) => {
    if (isSelected) return;

    const cameraModelName = extractCameraModelName(cameraModelInSelection);
    const cameraModelToExclude = cameraModels.find((model) => model.model === cameraModelName);

    exclusionCount += cameraModelToExclude?.count ?? 0;
  });

  return exclusionCount;
}

export function supportsCameraModelFiltering(
  provider: IFsLprProviderInterface,
  isWorksWithAxon: boolean,
): boolean {
  return provider.nameEnum === ViLPRProvider.AXON_VI || isWorksWithAxon;
}
